import { useCallback } from 'react';
import { PositionSelectType } from '@/utils/constant';
import { SearchFormType } from '../utils/config';
import { wgs84ToGcj02 } from '@/utils/coordTransform';
import { useSearchStore } from '../store/searchStore';
import { useEditorStore } from '../store/editorStore';
import { useDataStore } from '../store/dataStore';
import { useLayerStore } from '../store/layerStore';
import { useRoutePlanStore } from '../store/routePlanStore';
import { tileLayer } from '../utils/config';
import { message } from 'antd';
import type { MapEditorCoordinator } from '../controller/MapEditorCoordinator';

type PositionCoordinates = { lon: number; lat: number };

/**
 * SearchTools 业务回调钩子
 *
 * 层级：hooks（"React 生命周期"）
 *
 * 职责：
 * 1. 把地理位置搜索、站点/停靠点/车辆选择、图层显隐、绘制启停等操作
 *    从 index.tsx 中抽离，集中到本 hook
 * 2. 通过 coordinatorRef 调用 controller / manager 方法，不直接操作 SDK
 *
 * 返回的回调引用稳定，可直接传给 SearchTools
 */
export function useSearchTools(
  coordinatorRef: React.MutableRefObject<MapEditorCoordinator | null>,
  setPositionSearchVal: (fn: (prev: any) => any) => void,
  setLocationOptionList: (list: any[]) => void,
  setIsLocationDropdownOpen: (open: boolean) => void
) {

  /**
   * 更新地图中心并重新拉取动态地图列表
   */
  const updateDynamicMap = useCallback(
    (position: PositionCoordinates, rightSearch: any) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      ctrl.searchAndRefresh(position, rightSearch);
    },
    [coordinatorRef]
  );

  /**
   * 地理位置确认搜索
   */
  const confirmSearch = useCallback(
    (params: Partial<SearchFormType>) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      const position = params.value?.split(',');
      if (position) {
        ctrl.confirmSearch(params);
        ctrl.clearVehicleRectangle();
      }
    },
    [coordinatorRef]
  );

  /**
   * 根据车号/站点/停靠点查询基础位置
   */
  const getBasicPosition = useCallback(
    async (stationNum: any) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      const currentPosVal = useSearchStore.getState().positionSearchVal;
      const ok = await ctrl.getBasicPosition(
        stationNum,
        (pos) => updateDynamicMap(pos, null),
        currentPosVal.searchType
      );
      if (!ok) {
        message.error('该车地图信息缺失，请选择其他车号');
      }
    },
    [coordinatorRef, updateDynamicMap]
  );

  /**
   * 城市级联选择
   */
  const changeCascader = useCallback(
    (value: any) => {
      if (!value) return; // 点击清除按钮时 value 为 undefined
      getBasicPosition(value[1]);
    },
    [getBasicPosition]
  );

  /**
   * 停靠点/车辆选择
   */
  const handleSelectStop = useCallback(
    (value: any) => {
      getBasicPosition(value);
    },
    [getBasicPosition]
  );

  /**
   * 经纬度表单查询
   */
  const handleSelectlatlng = useCallback(
    (value: any) => {
      const [longitude, latitude] = wgs84ToGcj02(
        Number(value?.longitude),
        Number(value?.latitude)
      );
      updateDynamicMap({ lon: longitude, lat: latitude }, null);
      coordinatorRef.current?.clearVehicleRectangle();
    },
    [coordinatorRef, updateDynamicMap]
  );

  /**
   * 地理位置关键字搜索（带 200ms 防抖，由 SearchTools 内部 timer 管理）
   */
  const handleLocationSearch = useCallback(
    async (keyword: string) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl?.map) return;
      const searchText = keyword?.trim();
      if (!searchText) return;
      setPositionSearchVal((prev: any) => ({
        ...prev,
        searchType: PositionSelectType.position,
        value: searchText,
      }));
      ctrl.map.searchPois(searchText, (list: any) => {
        setLocationOptionList(list);
        setIsLocationDropdownOpen(list.length > 0);
      });
    },
    [coordinatorRef, setPositionSearchVal, setLocationOptionList, setIsLocationDropdownOpen]
  );

  /**
   * 图层显隐切换
   * 通过 store.getState() 读取最新值，避免闭包捕获导致回调重建
   */
  const changeLayerVisible = useCallback(
    (layerId: string) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      const state = useLayerStore.getState();
      const wmsLayerIds = new Set<string>(tileLayer.map((item) => item.layerId));
      ctrl.layerVisibility.toggleLayerVisible(
        layerId,
        wmsLayerIds,
        state.hiddenDynamicLayerIds,
        state.hiddenWmsLayerIds,
        state.toggleDynamicLayer,
        state.toggleWmsLayer
      );
    },
    [coordinatorRef]
  );

  /**
   * 开始绘制动态地图
   */
  const startPaint = useCallback(() => {
    const ctrl = coordinatorRef.current;
    if (!ctrl) return;
    ctrl.clearPolygonPopups();
    const curData = useEditorStore.getState().session.activePolygonData;
    if (curData) {
      ctrl.highlightMap(curData);
    }
    useEditorStore.getState().setCreateDrawingMode();
    ctrl.map.resetDrawingStyle();
    ctrl.startDraw();
  }, [coordinatorRef]);

  /**
   * 取消绘制
   */
  const cancelPaint = useCallback(() => {
    const ctrl = coordinatorRef.current;
    if (!ctrl) return;
    ctrl.cancelDraw();
    const draft = useEditorStore.getState().session.draft;
    if (draft?.id) {
      ctrl.map.deletePolygon(draft.id);
    }
    useEditorStore.getState().setBrowseMode();
  }, [coordinatorRef]);

  /**
   * 路径规划模式开关：进入/退出路径规划（读 store 最新值判断当前模式）
   */
  const toggleRoutePlan = useCallback(() => {
    const ctrl = coordinatorRef.current;
    if (!ctrl) return;
    if (useRoutePlanStore.getState().planningMode) {
      ctrl.routePlan.exitMode();
    } else {
      ctrl.routePlan.enterMode();
    }
  }, [coordinatorRef]);

  /**
   * 搜索下拉失焦/聚焦
   */
  const handleBlur = useCallback(() => {
    setIsLocationDropdownOpen(false);
  }, [setIsLocationDropdownOpen]);

  const handleFocus = useCallback(
    (hasOptions: boolean) => {
      setIsLocationDropdownOpen(hasOptions);
    },
    [setIsLocationDropdownOpen]
  );

  return {
    confirmSearch,
    handleLocationSearch,
    changeCascader,
    handleSelectStop,
    handleSelectlatlng,
    changeLayerVisible,
    startPaint,
    cancelPaint,
    toggleRoutePlan,
    handleBlur,
    handleFocus,
  };
}
